import React, { useEffect, useCallback, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useReactFlow } from '@xyflow/react';
import { DemoProvider, useDemo } from './DemoProvider';
import { DemoCursor } from './DemoCursor';
import { DemoBubble } from './DemoBubble';
import { useDemoCoordinates } from './useDemoCoordinates';
import { useDemoAudio } from './useDemoAudio';
import { getDemoScript } from './demoScripts';
import { Volume2 } from 'lucide-react';
import type { DemoStep } from '../../types/demo';

interface DemoOverlayProps {
  /** Which demo script to load (e.g., 'call-center') */
  scriptId: string;
  /** Whether the overlay is enabled/visible */
  enabled: boolean;
  /** Called when user dismisses the demo */
  onComplete: () => void;
}

/** Inner component that uses DemoProvider context */
const DemoOverlayInner: React.FC<DemoOverlayProps> = ({ scriptId, enabled, onComplete }) => {
  const { state, dispatch, loadScript, start, pause, resume, stop, advanceStep, jumpToStep, currentStep } = useDemo();
  const { updateNodeData } = useReactFlow();
  const coords = useDemoCoordinates();
  const stepTimeoutRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const cursorAnimRef = useRef<ReturnType<typeof setTimeout>>(undefined);
  const lastExecutedStepRef = useRef<number>(-1);
  const [waitingToStart, setWaitingToStart] = useState(false);
  const injectedNodesRef = useRef<string[]>([]);
  const signupAudioRef = useRef<HTMLAudioElement | null>(null);
  const signupPausedRef = useRef(false);

  const onAudioEnd = useCallback(() => {
    dispatch({ type: 'SET_AUDIO_STATE', state: 'idle' });
    // Check if current step has a decision
    if (currentStep?.decision) {
      dispatch({ type: 'ENTER_DECISION', decision: currentStep.decision });
    } else {
      // Auto-advance after delay
      const delay = currentStep?.autoAdvanceAfter ?? 300;
      stepTimeoutRef.current = setTimeout(() => advanceStep(), delay);
    }
  }, [dispatch, currentStep, advanceStep]);

  const onAudioStart = useCallback(() => {
    dispatch({ type: 'SET_AUDIO_STATE', state: 'playing' });
  }, [dispatch]);

  const audio = useDemoAudio({ onAudioEnd, onAudioStart });

  // Load script when enabled
  useEffect(() => {
    if (enabled && scriptId) {
      const script = getDemoScript(scriptId);
      if (script) {
        loadScript(script);
      }
    }
  }, [enabled, scriptId, loadScript]);

  // Show start button once script is loaded
  useEffect(() => {
    if (state.phase === 'idle' && state.steps.length > 0 && enabled) {
      setWaitingToStart(true);
    }
  }, [state.phase, state.steps.length, enabled]);

  // User clicked Start — this is a user gesture so audio is unlocked
  const handleStartDemo = useCallback(() => {
    setWaitingToStart(false);
    lastExecutedStepRef.current = -1;
    audio.startBgMusic('https://bigsplice-demo-audio.s3.amazonaws.com/bg-music.mp3?v2');
    start();
  }, [audio, start]);

  // Execute current step
  useEffect(() => {
    if (!currentStep) return;
    if (state.phase !== 'playing' && state.phase !== 'intro') return;

    // Guard: skip if we already executed this step index (prevents double-play
    // when phase changes from intro→playing within the same step)
    if (lastExecutedStepRef.current === state.currentStepIndex) return;
    lastExecutedStepRef.current = state.currentStepIndex;

    // Clear previous timers
    clearTimeout(stepTimeoutRef.current);
    clearTimeout(cursorAnimRef.current);

    const executeStep = async () => {
      const step = currentStep;

      // Delay before step
      if (step.delayBefore) {
        await new Promise((resolve) => setTimeout(resolve, step.delayBefore));
      }

      // Zoom viewport: full view for untargeted steps, zoom to node for targeted
      if (step.target?.nodeId) {
        coords.fitView({ nodes: [{ id: step.target.nodeId }], padding: 0.6, duration: 700 });
        await new Promise((resolve) => setTimeout(resolve, 350));
      } else if (!step.target) {
        coords.fitView({ padding: 0.15, duration: 1000 });
        await new Promise((resolve) => setTimeout(resolve, 400));
      }

      // Move cursor to target
      if (step.target) {
        const center = coords.resolveTargetCenter(step.target);
        if (center) {
          dispatch({ type: 'SET_CURSOR_TARGET', target: center });
          dispatch({ type: 'SET_CURSOR', position: center });
        }
      }

      // Execute cursor path (waypoints relative to target center)
      if (step.cursorPath && step.cursorPath.length > 0 && step.target) {
        const baseCenter = coords.resolveTargetCenter(step.target);
        if (baseCenter) {
          for (const wp of step.cursorPath) {
            const pos = { x: baseCenter.x + wp.x, y: baseCenter.y + wp.y };
            dispatch({ type: 'SET_CURSOR', position: pos });
            await new Promise((resolve) => setTimeout(resolve, wp.duration));
          }
        }
      }

      // Set subtitle
      if (step.subtitle) {
        dispatch({ type: 'SET_SUBTITLE', text: step.subtitle });
      }

      // Execute inject: push data into ReactFlow nodes
      if (step.inject) {
        updateNodeData(step.inject.nodeId, step.inject.data);
        if (!injectedNodesRef.current.includes(step.inject.nodeId)) {
          injectedNodesRef.current.push(step.inject.nodeId);
        }
      }

      // Execute interaction (e.g., type into search input)
      if (step.interaction && step.interaction.action === 'type' && step.target?.nodeId) {
        const nodeEl = document.querySelector(`[data-id="${step.target.nodeId}"]`);
        const input = nodeEl?.querySelector('input:not([type="file"]):not([type="hidden"])') as HTMLInputElement | null;
        if (input) {
          input.focus();
          const nativeInputValueSetter = Object.getOwnPropertyDescriptor(
            window.HTMLInputElement.prototype, 'value'
          )?.set;
          const text = step.interaction.value || '';
          for (let i = 0; i < text.length; i++) {
            nativeInputValueSetter?.call(input, text.slice(0, i + 1));
            input.dispatchEvent(new Event('input', { bubbles: true }));
            await new Promise((r) => setTimeout(r, step.interaction!.delay));
          }
        }
      }

      // Play audio
      if (step.audio) {
        dispatch({ type: 'SET_AUDIO_STATE', state: 'loading' });
        await audio.play(step.audio);
      } else if (step.type === 'decision' && step.decision) {
        // No audio, go straight to decision
        dispatch({ type: 'ENTER_DECISION', decision: step.decision });
      } else {
        // No audio - auto-advance after subtitle display time
        const delay = step.autoAdvanceAfter ?? Math.max(800, (step.subtitle?.length ?? 0) * 25);
        stepTimeoutRef.current = setTimeout(() => advanceStep(), delay);
      }

      // Update phase
      if (state.phase === 'intro') {
        dispatch({ type: 'SET_PHASE', phase: 'playing' });
      }
    };

    executeStep();
  }, [currentStep, state.phase, state.currentStepIndex]); // eslint-disable-line react-hooks/exhaustive-deps

  // Handle ESC key to dismiss
  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleDismiss();
      } else if (e.key === ' ' && state.phase !== 'agent-qa') {
        e.preventDefault();
        if (state.phase === 'paused') resume();
        else pause();
      }
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [state.phase]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for signup prompt — pause demo and play encouragement narration
  useEffect(() => {
    const handleSignupPrompt = () => {
      const isActive = state.phase === 'playing' || state.phase === 'intro' || state.phase === 'decision';
      if (!isActive) return;

      // Pause current demo audio and step timers
      audio.pause();
      pause();
      clearTimeout(stepTimeoutRef.current);
      signupPausedRef.current = true;

      // Show encouragement subtitle
      dispatch({
        type: 'SET_SUBTITLE',
        text: "Nice — go ahead and sign up. It only takes a minute. Once you're in, you can upload your own files and run them through this entire pipeline. I'll be right here when you get back.",
      });
      dispatch({ type: 'SET_AUDIO_STATE', state: 'playing' });

      // Play encouragement audio via a separate Audio element (avoids interfering with onAudioEnd)
      if (!signupAudioRef.current) {
        signupAudioRef.current = new Audio();
      }
      const sa = signupAudioRef.current;
      sa.src = 'https://bigsplice-demo-audio.s3.amazonaws.com/signup-encouragement.mp3?v5';
      sa.onended = () => {
        dispatch({ type: 'SET_AUDIO_STATE', state: 'idle' });
      };
      sa.play().catch(() => {
        // Autoplay blocked — just show the subtitle
        dispatch({ type: 'SET_AUDIO_STATE', state: 'idle' });
      });
    };

    window.addEventListener('bigsplice:signup-prompt', handleSignupPrompt);
    return () => window.removeEventListener('bigsplice:signup-prompt', handleSignupPrompt);
  }, [state.phase, audio, pause, dispatch]); // eslint-disable-line react-hooks/exhaustive-deps

  // Listen for successful file upload — resume demo from where it left off
  useEffect(() => {
    const handleFileUploaded = () => {
      if (!signupPausedRef.current) return;
      signupPausedRef.current = false;

      // Clear the encouragement subtitle and resume
      dispatch({ type: 'SET_SUBTITLE', text: '' });
      resume();
      audio.resume();
    };

    window.addEventListener('bigsplice:file-uploaded', handleFileUploaded);
    return () => window.removeEventListener('bigsplice:file-uploaded', handleFileUploaded);
  }, [resume, audio, dispatch]);

  const handleDismiss = useCallback(() => {
    audio.stop();
    if (signupAudioRef.current) {
      signupAudioRef.current.pause();
      signupAudioRef.current.src = '';
    }
    signupPausedRef.current = false;
    setWaitingToStart(false);
    clearTimeout(stepTimeoutRef.current);
    clearTimeout(cursorAnimRef.current);
    // Clean up injected demo state from nodes
    for (const nodeId of injectedNodesRef.current) {
      updateNodeData(nodeId, { demoState: undefined, selectedFile: undefined });
    }
    injectedNodesRef.current = [];
    stop();
    onComplete();
  }, [audio, stop, onComplete, updateNodeData]);

  const handleDecisionSelect = useCallback(
    (nextStepId: string) => {
      if (nextStepId === '__replay') {
        lastExecutedStepRef.current = -1;
        start();
        return;
      }
      // Reset guard so the jumped-to step can execute
      lastExecutedStepRef.current = -1;
      dispatch({ type: 'SELECT_DECISION', nextStepId });
    },
    [dispatch, start],
  );

  const handleTogglePause = useCallback(() => {
    if (state.phase === 'paused') {
      resume();
      audio.resume();
    } else {
      pause();
      audio.pause();
    }
  }, [state.phase, pause, resume, audio]);

  const handleAskQuestion = useCallback(() => {
    audio.pause();
    dispatch({ type: 'ENTER_AGENT_QA' });
  }, [audio, dispatch]);

  if (!enabled || (state.phase === 'idle' && !waitingToStart)) return null;

  // Waiting for user to click Start
  if (waitingToStart) {
    return createPortal(
      <div
        className="fixed inset-0 flex items-center justify-center"
        style={{ zIndex: 9999 }}
      >
        <div className="text-center rounded-2xl bg-background/80 backdrop-blur-sm border border-border/50 px-6 sm:px-12 py-8 sm:py-10 shadow-lg mx-4">
          <p className="font-mono text-sm text-muted-foreground tracking-widest uppercase mb-4">
            Guided Demo
          </p>
          <button
            onClick={handleStartDemo}
            className="inline-flex items-center gap-2 px-8 py-3 rounded-xl bg-primary text-primary-foreground font-medium text-lg hover:bg-primary/90 transition-colors"
          >
            <Volume2 className="h-5 w-5" />
            Start Demo
          </button>
          <p className="mt-4 text-sm text-muted-foreground">
            Audio narration will guide you through the features
          </p>
        </div>
      </div>,
      document.body,
    );
  }

  const overlayContent = (
    <>
      <DemoCursor
        x={state.cursorPosition.x}
        y={state.cursorPosition.y}
        visible={state.cursorVisible}
        audioPlaying={state.audioState === 'playing'}
      />
      <DemoBubble
        cursorX={state.cursorPosition.x}
        cursorY={state.cursorPosition.y}
        visible={true}
        phase={state.phase}
        subtitle={state.subtitleText}
        audioPlaying={state.audioState === 'playing'}
        decision={state.activeDecision}
        onDecisionSelect={handleDecisionSelect}
        onAskQuestion={handleAskQuestion}
        onDismiss={handleDismiss}
        onTogglePause={handleTogglePause}
        paused={state.phase === 'paused'}
      />
    </>
  );

  return createPortal(overlayContent, document.body);
};

/**
 * Reusable demo overlay that can be layered on any flow page.
 * Renders as a portal above the existing flow canvas.
 *
 * Usage:
 * ```tsx
 * <DemoOverlay scriptId="call-center" enabled={showDemo} onComplete={() => setShowDemo(false)} />
 * ```
 */
export const DemoOverlay: React.FC<DemoOverlayProps> = (props) => {
  if (!props.enabled) return null;

  return (
    <DemoProvider>
      <DemoOverlayInner {...props} />
    </DemoProvider>
  );
};
